import axios from "axios";
import type { AxiosInstance, InternalAxiosRequestConfig } from "axios";

import { loggedOut } from "@/store/authSlice";
import { store } from "@/store";

// Vite proxy maps /api → emp_api in dev (see vite.config.ts). In prod,
// VITE_API_BASE should be set at build time so the bundle hits the deployed
// emp_api directly.
const baseURL = (import.meta.env.VITE_API_BASE as string | undefined) || "";

export const api: AxiosInstance = axios.create({
  baseURL,
  // Keep timeout generous; cal_index can churn through prices joins.
  timeout: 30_000,
});

// Attach Authorization header from current Redux auth state.
api.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const token = store.getState().auth.token;
  if (token) {
    config.headers.set("Authorization", `Bearer ${token}`);
  }
  return config;
});

// 401 → boot user out. Component-level handlers can still see the rejection
// to render their own message.
api.interceptors.response.use(
  (resp) => resp,
  (err) => {
    if (err?.response?.status === 401) {
      store.dispatch(loggedOut());
    }
    return Promise.reject(err);
  },
);

// ============================================================ login
// emp_api uses OAuth2 password-grant, so the token endpoint expects
// application/x-www-form-urlencoded — not JSON. urlsearchparams body forces
// the right content-type.
import type {
  AdminBanner, AdminUser, Banner, BenchmarkPoint, EmailList,
  IndexPoint, IndexStatistics, MeResponse, MpInfo, NavRange,
  ReportKind, ReportPreview, ReportSendResult,
  ScoreResponse, TokenResponse, WeightRow,
} from "@/types/api";

export async function postLoginToken(username: string, password: string): Promise<TokenResponse> {
  const body = new URLSearchParams();
  body.set("username", username);
  body.set("password", password);
  const { data } = await api.post<TokenResponse>("/api/login/token", body, {
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
  });
  return data;
}

export async function getMe(): Promise<MeResponse> {
  const { data } = await api.get<MeResponse>("/api/login/me");
  return data;
}

// ============================================================ product
export async function listProducts(productLine?: string): Promise<MpInfo[]> {
  const { data } = await api.get<MpInfo[]>("/api/product/list", {
    params: productLine ? { product_line: productLine } : undefined,
  });
  return data;
}

export async function getPortfolio(mpCode: string, on?: string): Promise<WeightRow[]> {
  const { data } = await api.get<WeightRow[]>(
    `/api/product/${encodeURIComponent(mpCode)}/portfolio`,
    { params: on ? { on } : undefined },
  );
  return data;
}

export async function getPortfolioDates(mpCode: string): Promise<string[]> {
  const { data } = await api.get<string[]>(
    `/api/product/${encodeURIComponent(mpCode)}/portfolio/dates`,
  );
  return data;
}

// Server-built CSV (columns ticker, assetClass, name, weights, changes).
// We can't use a plain <a download> because the API requires the bearer
// token; fetch as blob and trigger the download manually.
export async function downloadPortfolioCsv(
  mpCode: string, on?: string,
): Promise<void> {
  const resp = await api.get<Blob>(
    `/api/product/${encodeURIComponent(mpCode)}/portfolio.csv`,
    { responseType: "blob", params: on ? { on } : undefined },
  );
  const disp = (resp.headers["content-disposition"] as string | undefined) ?? "";
  let filename = `${mpCode}_portfolio.csv`;
  const m = /filename="?([^";]+)"?/.exec(disp);
  if (m) filename = m[1];

  const url = URL.createObjectURL(resp.data);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export async function getIndex(
  mpCode: string,
  range?: { start?: string; end?: string },
): Promise<IndexPoint[]> {
  const { data } = await api.get<IndexPoint[]>(
    `/api/product/${encodeURIComponent(mpCode)}/index`,
    { params: range && (range.start || range.end) ? range : undefined },
  );
  return data;
}

export async function getEmails(mpCode: string): Promise<EmailList> {
  const { data } = await api.get<EmailList>(
    `/api/product/${encodeURIComponent(mpCode)}/emails`,
  );
  return data;
}

export async function getStatistics(
  mpCode: string,
  range?: { start?: string; end?: string },
): Promise<IndexStatistics> {
  const { data } = await api.get<IndexStatistics>(
    `/api/product/${encodeURIComponent(mpCode)}/statistics`,
    { params: range && (range.start || range.end) ? range : undefined },
  );
  return data;
}

export async function getScore(mpCode: string): Promise<ScoreResponse> {
  const { data } = await api.get<ScoreResponse>(
    `/api/product/${encodeURIComponent(mpCode)}/score`,
  );
  return data;
}

export async function getNavRange(mpCode: string): Promise<NavRange> {
  const { data } = await api.get<NavRange>(
    `/api/product/${encodeURIComponent(mpCode)}/nav_range`,
  );
  return data;
}

// ============================================================ quantstat
// Streams a server-generated quantstats HTML tearsheet. We can't use a plain
// <a download> because the API requires the bearer token; fetch as blob and
// trigger the download manually.
export async function downloadQuantstatHtml(
  mpCode: string, benchmark: string | null, start?: string, end?: string,
): Promise<void> {
  const params: Record<string, string> = {};
  if (benchmark) params.benchmark = benchmark;
  if (start) params.start = start;
  if (end) params.end = end;
  const resp = await api.get<Blob>(
    `/api/product/${encodeURIComponent(mpCode)}/quantstat`,
    {
      responseType: "blob",
      params: Object.keys(params).length > 0 ? params : undefined,
      // quantstats is slow (multi-second chart render); the global 30s
      // axios timeout can fire before the file is ready.
      timeout: 120_000,
    },
  );
  // Prefer the server-suggested filename in Content-Disposition; fall back
  // to a sensible default if missing.
  const disp = (resp.headers["content-disposition"] as string | undefined) ?? "";
  let filename = `${mpCode}_quantstat.html`;
  const m = /filename="?([^";]+)"?/.exec(disp);
  if (m) filename = m[1];

  const url = URL.createObjectURL(resp.data);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// User-uploaded NAV CSV → server-rendered quantstats HTML tearsheet.
// Same blob-download dance as the per-mp_code variant.
export async function downloadQuantstatHtmlFromCsv(
  file: File, title?: string,
): Promise<void> {
  const form = new FormData();
  form.append("file", file);
  if (title) form.append("title", title);
  const resp = await api.post<Blob>(
    `/api/product/quantstat/upload`,
    form,
    {
      responseType: "blob",
      timeout: 120_000,
    },
  );
  const disp = (resp.headers["content-disposition"] as string | undefined) ?? "";
  const fallback = (file.name.replace(/\.[^.]+$/, "") || "upload") + "_quantstat.html";
  let filename = fallback;
  const m = /filename="?([^";]+)"?/.exec(disp);
  if (m) filename = m[1];

  const url = URL.createObjectURL(resp.data);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// ============================================================ benchmark
export async function listBenchmarks(mpCode: string): Promise<string[]> {
  const { data } = await api.get<string[]>(
    `/api/benchmark/${encodeURIComponent(mpCode)}`,
  );
  return data;
}

export async function getBenchmarkSeries(
  mpCode: string,
  benchmark: string,
  range?: { start?: string; end?: string },
): Promise<BenchmarkPoint[]> {
  const { data } = await api.get<BenchmarkPoint[]>(
    `/api/benchmark/${encodeURIComponent(mpCode)}/${encodeURIComponent(benchmark)}`,
    { params: range && (range.start || range.end) ? range : undefined },
  );
  return data;
}

export async function postRefreshToken(): Promise<TokenResponse> {
  const { data } = await api.post<TokenResponse>("/api/login/refresh");
  return data;
}

// ============================================================ banner
export async function listBanners(): Promise<Banner[]> {
  const { data } = await api.get<Banner[]>("/api/banner");
  return data;
}

// ============================================================ report (admin)
export async function getReportPreview(
  mpCode: string, kind: ReportKind,
): Promise<ReportPreview> {
  const { data } = await api.get<ReportPreview>("/api/report/preview", {
    params: { mp_code: mpCode, kind },
  });
  return data;
}

export async function reportSendInternal(mpCode: string): Promise<ReportSendResult> {
  const { data } = await api.post<ReportSendResult>(
    "/api/report/send_internal", { mp_code: mpCode },
  );
  return data;
}

export async function reportSendExternal(mpCode: string): Promise<ReportSendResult> {
  const { data } = await api.post<ReportSendResult>(
    "/api/report/send_external", { mp_code: mpCode },
  );
  return data;
}

// ============================================================ admin: users
export async function adminListUsers(): Promise<AdminUser[]> {
  const { data } = await api.get<AdminUser[]>("/api/admin/users");
  return data;
}

export async function adminCreateUser(req: {
  user_id: string;
  password: string;
  display_name?: string;
  is_active?: boolean;
  is_admin?: boolean;
}): Promise<void> {
  await api.post("/api/admin/users", req);
}

export async function adminUpdateUser(userId: string, patch: {
  display_name?: string;
  is_active?: boolean;
  is_admin?: boolean;
}): Promise<void> {
  await api.patch(`/api/admin/users/${encodeURIComponent(userId)}`, patch);
}

export async function adminDeleteUser(userId: string): Promise<void> {
  await api.delete(`/api/admin/users/${encodeURIComponent(userId)}`);
}

export async function adminResetPassword(userId: string, password: string): Promise<void> {
  await api.post(`/api/admin/users/${encodeURIComponent(userId)}/reset_password`, { password });
}

// ============================================================ admin: grants
export async function adminListGrants(userId: string): Promise<string[]> {
  const { data } = await api.get<string[]>(`/api/admin/users/${encodeURIComponent(userId)}/grants`);
  return data;
}

export async function adminGrantMpCode(userId: string, mpCode: string): Promise<void> {
  await api.post(`/api/admin/users/${encodeURIComponent(userId)}/grants`, { mp_code: mpCode });
}

export async function adminRevokeMpCode(userId: string, mpCode: string): Promise<void> {
  await api.delete(
    `/api/admin/users/${encodeURIComponent(userId)}/grants/${encodeURIComponent(mpCode)}`,
  );
}

// ============================================================ admin: products / banners
export async function adminListProducts(): Promise<MpInfo[]> {
  const { data } = await api.get<MpInfo[]>("/api/admin/products");
  return data;
}

export async function adminListBanners(): Promise<AdminBanner[]> {
  const { data } = await api.get<AdminBanner[]>("/api/admin/banners");
  return data;
}

export async function adminCreateBanner(req: {
  file_nm: string;
  link: string;
  sort_order?: number;
  is_active?: boolean;
}): Promise<{ id: number }> {
  const { data } = await api.post<{ id: number }>("/api/admin/banners", req);
  return data;
}

export async function adminUpdateBanner(id: number, patch: {
  file_nm?: string;
  link?: string;
  sort_order?: number;
  is_active?: boolean;
}): Promise<void> {
  await api.patch(`/api/admin/banners/${id}`, patch);
}

export async function adminDeleteBanner(id: number): Promise<void> {
  await api.delete(`/api/admin/banners/${id}`);
}
